import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';

import ConfigContext from '../../utils/ConfigContext/ConfigContext';

import './FloorPlanFinancialPlans.scss';

/**
 * FloorPlanFinancialPlans component displays fee information in a table format.
 */
const FloorPlanFinancialPlans = ( { hasFeePlanNames, planMap, className } ) => {
  // No plans to display
  if ( !planMap || Object.keys( planMap ).length === 0 ) {
    return null;
  }

  const {
    featureFlags,
    showPricing,
    setShowPricing,
    setUnitHasDisplayableFees,
  } = useContext( ConfigContext );

  // Should we show pricing?
  useEffect( () => {
    setShowPricing( featureFlags?.platformShowPricing );
  }, [ featureFlags ] );

  // If a floor plan has pricing data and can show that data, set context to show
  // that the floor plan has fees.
  const hasPlanMap = planMap && Object.keys( planMap ).length > 0;

  useEffect( () => {
    // Only set that unit has displayable fees, but don't automatically display them
    // User will still need to click "more" to see them
    if ( hasPlanMap && showPricing ) {
      setUnitHasDisplayableFees( true );
    } else {
      setUnitHasDisplayableFees( false );
    }
  }, [ showPricing, hasPlanMap ] );

  // Check if any plan has a monthly fee value
  const anyPlanHasMonthlyFee = Object.values( planMap ).some(
    ( plan ) => plan.monthlyFee && plan.monthlyFee.formatted,
  );

  return showPricing && (
    <div className={`floorPlanStatusWidget floorPlanFinancialPlans ${className || ''} ${hasFeePlanNames ? 'floorPlanFinancialPlans--withPlanNames' : 'floorPlanFinancialPlans--noPlanNames'}`}>
      {/* Mobile view with card layout */}
      {Object.entries( planMap ).map( ( [ planId, plan ] ) => (
        <div key={`mobile-${planId}`} className="mobile-view">
          {hasFeePlanNames && (
            <div className="floorPlanFinancialPlans__plan-header">
              {plan.planName}
            </div>
          )}

          <div className={`floorPlanFinancialPlans__fee-card ${hasFeePlanNames ? 'has-header' : ''}`}>
            {/* Entrance Fee section */}
            {plan.entranceFee && plan.entranceFee.formatted && (
              <>
                <div className="floorPlanFinancialPlans__fee-type">Entrance Fee</div>
                <div className="floorPlanFinancialPlans__amount">
                  {plan.entranceFee.formatted}
                </div>
                {plan.entranceSecondPerson && plan.entranceSecondFee && (
                <div className="floorPlanFinancialPlans__secondPerson">
                  Second Person:
                  {' '}
                  {plan.entranceSecondFee.formatted}
                </div>
                )}
              </>
            )}

            {/* Monthly Fee section - only show if it exists */}
            {plan.monthlyFee && plan.monthlyFee.formatted && (
              <>
                <div className="floorPlanFinancialPlans__fee-type">Monthly Fee</div>
                <div className="floorPlanFinancialPlans__amount">
                  {plan.monthlyFee.formatted}
                </div>
                {plan.monthlySecondPerson && plan.monthlySecondFee && (
                <div className="floorPlanFinancialPlans__secondPerson">
                  Second Person:
                  {' '}
                  {plan.monthlySecondFee.formatted}
                </div>
                )}
              </>
            )}
          </div>
        </div>
      ) )}

      {/* Desktop view with table layout */}
      <table>
        <thead>
          <tr>
            {hasFeePlanNames && <th />}
            <th className={hasFeePlanNames ? '' : 'floorPlanFinancialPlans__firstColumn'}>Entrance Fee</th>
            {anyPlanHasMonthlyFee && <th>Monthly Fee</th>}
          </tr>
        </thead>
        <tbody>
          {Object.entries( planMap ).map( ( [ planId, plan ] ) => (
            <tr key={planId}>

              {hasFeePlanNames && (
                <td className="floorPlanFinancialPlans__planName">
                  {plan.planName || ''}
                </td>
              )}

              <td className={hasFeePlanNames ? '' : 'floorPlanFinancialPlans__firstColumn'}>
                <div className="floorPlanFinancialPlans__amount">
                  {plan.entranceFee?.formatted}
                </div>
                {plan.entranceSecondPerson && plan.entranceSecondFee && (
                  <div className="floorPlanFinancialPlans__secondPerson">
                    Second Person:
                    {' '}
                    {plan.entranceSecondFee.formatted}
                  </div>
                )}
              </td>

              {anyPlanHasMonthlyFee && (
                <td>
                  <div className="floorPlanFinancialPlans__amount">
                    {plan.monthlyFee?.formatted}
                  </div>
                  {plan.monthlySecondPerson && plan.monthlySecondFee && (
                    <div className="floorPlanFinancialPlans__secondPerson">
                      Second Person:
                      {' '}
                      {plan.monthlySecondFee.formatted}
                    </div>
                  )}
                </td>
              )}
            </tr>
          ) )}
        </tbody>
      </table>
    </div>
  );
};

FloorPlanFinancialPlans.propTypes = {
  hasFeePlanNames: PropTypes.bool,
  planMap: PropTypes.object,
  className: PropTypes.string,
};

FloorPlanFinancialPlans.defaultProps = {
  hasFeePlanNames: false,
  planMap: null,
  className: '',
};

export default FloorPlanFinancialPlans;
